import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { AuthService, AuthUser } from '../services/authService';

interface LoginProps {
  onSignedIn: (user: AuthUser) => void;
}

export const Login: React.FC<LoginProps> = ({ onSignedIn }) => {
  const [error, setError] = useState<string | null>(null);

  // Set only when the server reports must_reset_password=true — switches
  // this screen into "define a new password" mode using the short-lived
  // ticket instead of a normal session.
  const [resetTicket, setResetTicket] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const result = await AuthService.signIn({ email, password });

    if (result.error) {
      setError(result.error);
    } else if (result.mustResetPassword && result.resetTicket) {
      setResetTicket(result.resetTicket);
    } else if (result.user) {
      onSignedIn(result.user);
    }
  };

  const handleForcePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmNewPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      const { user, error: err } = await AuthService.forcePasswordChange(resetTicket!, newPassword, confirmNewPassword);
      if (err) throw new Error(err);
      if (user) onSignedIn(user);
    } catch (e: any) {
      setError(e.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setSubmitting(false);
    }
  };

  if (resetTicket) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="bg-white max-w-md w-full p-8 rounded-2xl shadow-lg border border-neutral-200">
          <div className="text-center mb-6">
            <h1 className="text-xl font-medium text-neutral-800">Define una contraseña nueva</h1>
            <p className="text-sm text-neutral-500 mt-2">
              Por seguridad, todos los usuarios deben definir una contraseña nueva la primera vez que inician sesión con este sistema.
            </p>
            <p className="text-xs text-neutral-500 mt-2">
              Mínimo 12 caracteres, con mayúscula, minúscula, número y símbolo.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleForcePasswordChange} className="space-y-4">
            <div>
              <label htmlFor="force-new-password" className="block text-sm font-medium text-neutral-700 mb-1">
                Nueva contraseña
              </label>
              <div className="relative">
                <input
                  id="force-new-password"
                  type={showNewPass ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-3 pr-11 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                  required
                  autoFocus
                />
                <button type="button" onClick={() => setShowNewPass((p) => !p)}
                  aria-label={showNewPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-neutral-600">
                  {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="force-confirm-password" className="block text-sm font-medium text-neutral-700 mb-1">
                Confirmar contraseña
              </label>
              <input
                id="force-confirm-password"
                type={showNewPass ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-[#002a5c] transition-colors disabled:opacity-60"
            >
              {submitting ? 'Guardando...' : 'Guardar y continuar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <div className="bg-white max-w-md w-full p-8 rounded-2xl shadow-lg border border-neutral-200">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 bg-[#003D7F] rounded-xl flex items-center justify-center text-white text-3xl font-bold shadow-lg">
              LZ
            </div>
            <span className="text-2xl font-bold text-neutral-800">Expense</span>
          </div>
          <h1 className="text-xl font-medium text-neutral-600">
            {'Inicia sesión para continuar'}
          </h1>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSignIn} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-neutral-700 mb-1">
              Email
            </label>
            <input
              id="login-email"
              name="email"
              type="email"
              placeholder="tu.email@empresa.com"
              className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary outline-none"
              required
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-neutral-700 mb-1">
              Contraseña
            </label>
            <input
              id="login-password"
              name="password"
              type="password"
              placeholder="••••••••"
              className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary outline-none"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-[#002a5c] transition-colors"
          >
            Iniciar Sesión
          </button>
        </form>

        <Link to="/forgot-password" className="mt-4 block text-center text-sm text-neutral-500 hover:text-primary transition-colors">
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
    </div>
  );
};
